import JSZip from 'jszip'
import {parseOrErr, PlainObject, zx} from 'shared/type'
import {entries, fromEntries, partition, pipe, sortBy, unique} from 'remeda'
import {asyncMap, asyncIterableToArray} from 'modern-async'
import {jsonlParse} from 'shared/jsonl'
import {ok, err, okOrCatch, type Err, type OK} from 'shared/promise'
import {notNull} from 'shared/type'
import {QuestionForecastPub, ResolutionValue, zQuestionRow, type QuestionRow} from 'shared/forecast/question'
import {zSubqRow} from 'components/question/subq'
import {zForecastFetchedBase} from 'shared/forecast/forecastTypes'
import {zCommentRow} from 'components/discussion/commentTypes'
import {zReactionRow} from 'components/question/reactions'
import z from 'zod'
import {zProfileRow} from 'components/portal/profileTypes'
import {keys, mapEntries, notNullOrEmpty} from 'shared/collection'
import {match} from 'ts-pattern'
import {Forecast} from 'shared/forecast/forecast'
import {nanoid} from 'nanoid'
import {Resolution} from 'shared/forecast'
// Function to get collection file name given a slug and collection type
const collectionPath = (slug: string, collectionType: ExportTable): string => {
  if (collectionType === 'question') return `${slug}/question.json`
  return `${slug}/${collectionType}.jsonl`
}

const exportMd = {
  question: {
    question_id_col: 'id',
    profile_cols: ['created_by'],
    schema: zQuestionRow
  },
  subquestion: {schema: zSubqRow},
  forecast: {schema: zForecastFetchedBase},
  comment: {schema: zCommentRow.omit({subquestions: true})},
  reaction: {schema: zReactionRow},
  profile: {
    profile_cols: ['id'],
    schema: zProfileRow.omit({updated_at: true}).extend({email: zx.nullish.string})
  }
}

export type ExportTable = keyof typeof exportMd
export type ExportRow<T extends ExportTable> = z.infer<(typeof exportMd)[T]['schema']>

const defaultMd = {
  question_id_col: 'question_id',
  profile_cols: ['created_by']
}
type ExportMd = {
  schema: z.ZodObject<any>
  question_id_col: string
  profile_cols: string[]
  hasDeleted: boolean
  hasWorkspace: boolean
  hasSubqRelation: boolean
}
export const getExportMd = (table: ExportTable) => {
  const md = {...defaultMd, ...exportMd[table]}
  const hasDeleted = 'deleted' in md.schema.shape
  const hasWorkspace = 'workspace_id' in md.schema.shape
  const hasSubqRelation = 'subquestion_id' in md.schema.shape
  return {...md, hasDeleted, hasWorkspace, hasSubqRelation} as ExportMd
}
export const exportCollections = keys(exportMd) as ExportTable[]

// Private function to extract question data from zip files
const getQuestionDataFromZip = async (
  zip: JSZip,
  questionIds: string[] = []
): Promise<Err | OK<{slug: string; question: ExportRow<'question'>}[]>> => {
  const questionDirs = pipe(
    entries(zip.files)
      .filter(([path]) => path.includes('/') && !path.endsWith('/'))
      .map(([path]) => path.split('/')[0]!),
    ps => unique(ps)
  )
  const questionData = await asyncMap(Array.from(questionDirs), async (slug: string) => {
    const questionFile = zip.file(collectionPath(slug, 'question'))
    if (!questionFile) return err(`No question.json found in ${slug}`)
    const questionData = await okOrCatch(questionFile.async('string'))
    if (!questionData.ok) return err(`Failed to read question data in ${slug}: ${questionData.message}`)
    const parsedData = await okOrCatch(Promise.resolve(JSON.parse(questionData.value ?? '')))
    if (!parsedData.ok) return err(`Invalid JSON in ${slug}: ${parsedData.message}`)
    const questionParse = parseOrErr(zQuestionRow, parsedData)
    if (!questionParse.ok) return err(`Invalid question data in ${slug}: ${questionParse.message}`)
    if (questionIds.length > 0 && !questionIds.includes(questionParse.data.id)) return undefined
    return ok({slug, question: questionParse.data})
  }).then(rs => rs.filter(notNull))

  const [questionDataOk, questionDataErr] = partition(questionData, r => r.ok)
  if (questionDataErr.length > 0)
    return err(`Failed to read questions: ${questionDataErr.map(e => e.message).join('\n')}`)
  return ok(sortBy(questionDataOk, q => q.question.cfg.title).map(r => ({zip, slug: r.slug, question: r.question})))
}

export const openExport = async (file: File | ArrayBuffer | Buffer, questionIds: string[] = []) => {
  const zipBuffer = file instanceof File ? await file.arrayBuffer() : file
  const zip = new JSZip()
  await zip.loadAsync(zipBuffer)
  const errorsFile = zip.file('errors.txt')
  const exportErrors = errorsFile ? await errorsFile.async('string') : null

  const collectionItems = async <T extends ExportTable>(
    collectionName: T,
    path: string
  ): Promise<Err | OK<ExportRow<T>[]>> => {
    const file = zip.file(path)
    if (!file) return ok([])
    const [rowsOk, rowsErr] = await asyncIterableToArray(
      jsonlParse(await file.async('text'), getExportMd(collectionName).schema.omit({deleted: true}))
    ).then(rs => partition(rs, r => r.ok))
    if (rowsErr.length > 0) return err(`Failed to read ${collectionName}: ${rowsErr.map(e => e.message).join('\n')}`)
    return ok(rowsOk as any)
  }

  const getQuestionsWithCollections = async () => {
    const questionData = await getQuestionDataFromZip(zip, questionIds)
    if (!questionData.ok) return err(questionData)
    const questionsWithData = await asyncMap(questionData, async ({slug, question}) => {
      const [subquestions, forecasts, comments, reactions] = await Promise.all([
        collectionItems('subquestion', collectionPath(slug, 'subquestion')),
        collectionItems('forecast', collectionPath(slug, 'forecast')),
        collectionItems('comment', collectionPath(slug, 'comment')),
        collectionItems('reaction', collectionPath(slug, 'reaction'))
      ])
      if (!subquestions.ok) return err(subquestions)
      if (!forecasts.ok) return err(forecasts)
      if (!comments.ok) return err(comments)
      if (!reactions.ok) return err(reactions)
      return ok({question, subquestions, forecasts, comments, reactions})
    })
    const [questions, errors] = partition(questionsWithData, r => r.ok)
    if (errors.length > 0) return err(`Failed to read questions: ${errors.map(e => e.message).join('\n')}`)
    const profiles = await collectionItems('profile', 'profile.jsonl')
    if (!profiles.ok) return err(profiles)
    return ok({questions, profiles})
  }

  const getQuestions = async () => {
    const questionData = await getQuestionDataFromZip(zip, questionIds)
    if (!questionData.ok) return err(questionData)
    const hasForecasts = questionData.some(r => !!zip.file(collectionPath(r.slug, 'forecast')))
    const hasComments = questionData.some(r => !!zip.file(collectionPath(r.slug, 'comment')))
    return ok({questions: questionData.map(q => q.question), hasForecasts, hasComments})
  }

  return {
    getQuestionsWithCollections,
    getQuestions,
    getExportErrors: () => exportErrors,
    hasProfiles: () => !!zip.file('profile.jsonl'),
    zip
  }
}
export type ExportOpened = Awaited<ReturnType<typeof openExport>>
export type ExportData = Extract<Awaited<ReturnType<ExportOpened['getQuestionsWithCollections']>>, {ok: true}>
export type ExportDataQuestionWithCollections = ExportData['questions'][number]

export const newId = () => nanoid(8)

/** creates a new mapping without modifying any rows */
export const createMappingFromIds = <T extends {id: string}>(rows: T[]) => {
  const map = new Map<string, string>()
  rows.forEach(row => {
    if (!map.has(row.id)) map.set(row.id, newId())
  })
  return map
}

/** map ids in a row to new ids given in rowIdMappings. Include rowIdMappings if you want to map. This will also map the id if this is the same table as a provided mapping */
export const getMappedRow = <
  T extends {id: string} & Partial<{
    subquestion_id: string
    created_by: string
    question_id: string
    cfg: PlainObject
    parent_id: string
  }>
>({
  row,
  table,
  rowIdMappings,
  ensureIdMapped,
  createNewWhenMissing
}: {
  row: T
  table: ExportTable
  rowIdMappings: Partial<Record<ExportTable, Map<string, string>>>
  ensureIdMapped?: boolean
  /** if true, create a new id's when seen on a row if there is a rowIdMappings for the table and the id is not found */
  createNewWhenMissing?: boolean
}): OK<T> | Err => {
  const md = getExportMd(table)
  const profileCols = md.profile_cols ?? []
  const newRow = {...row} as any
  const {profile: profileIdMap, subquestion: subqIdMap, question: questionIdMap, comment: commentIdMap} = rowIdMappings
  const oldId = row.id

  const getMappedIdOrErr = (id: string, table: ExportTable): OK<{id: string}> | Err => {
    const idMap = rowIdMappings[table as keyof typeof rowIdMappings]
    if (!idMap) return err(`${table} mapping not found`)
    const idMapped = idMap.get(id)
    if (idMapped != null) return ok({id: idMapped})
    if (!createNewWhenMissing) return err(`${table} id ${id} not found in mapping`)
    const createdId = newId()
    idMap.set(id, createdId)
    return ok({id: createdId})
  }

  if (table in rowIdMappings) {
    const rowIdMaped = getMappedIdOrErr(row.id, table)
    if (!rowIdMaped.ok) return rowIdMaped
    newRow.id = rowIdMaped.id
  }

  if (profileIdMap && table != 'profile')
    profileCols.forEach(col => {
      const profileId = newRow[col] as string | undefined
      if (profileId) {
        newRow[col] = profileIdMap.get(profileId)!
      } else return err(`profile id not found for ${table} ${row.id}`)
    })

  if (subqIdMap) {
    if (row.subquestion_id) {
      const subqId = getMappedIdOrErr(row.subquestion_id, 'subquestion')
      if (!subqId.ok) return subqId
      newRow.subquestion_id = subqId.id
    }

    if (table == 'subquestion') {
      const subqId = getMappedIdOrErr(row.id, 'subquestion')
      if (!subqId.ok) return subqId
      newRow.id = subqId.id
      if (row.parent_id) {
        const parentId = getMappedIdOrErr(row.parent_id, 'subquestion')
        if (!parentId.ok) return err(`parent_id not found for ${table} ${row.id}`)
        newRow.parent_id = parentId.id
      }
    }
  }

  if (questionIdMap) {
    if (row.question_id) {
      const questionId = getMappedIdOrErr(row.question_id, 'question')
      if (!questionId.ok) return err(`question_id not found for ${table} ${row.id}`)
      newRow.question_id = questionId.id
    }
  }

  if (profileIdMap) {
    if (row.created_by) {
      const createdById = getMappedIdOrErr(row.created_by, 'profile')
      if (!createdById.ok) return err(`created_by not found for ${table} ${row.id}`)
      newRow.created_by = createdById.id
    }
  }

  if (commentIdMap && table == 'comment') {
    if (row.parent_id) {
      const parentId = getMappedIdOrErr(row.parent_id, 'comment')
      if (!parentId.ok) return err(`parent_id not found for ${table} ${row.id}`)
      newRow.parent_id = parentId.id
    }
    const newCommentId = getMappedIdOrErr(row.id, 'comment')
    if (!newCommentId.ok) return err(`comment_id not found for ${table} ${row.id}`)
    newRow.id = newCommentId.id
  }

  if (row.cfg) {
    const newCfg = match(table)
      .with('question', () => {
        const cfg = {...row.cfg} as QuestionRow['cfg']
        if (cfg.publish && (subqIdMap || profileIdMap)) {
          cfg.publish = {
            ...cfg.publish,
            forecasts: fromEntries(
              mapEntries(cfg.publish.forecasts, (k, v) => {
                const keyValue = Forecast.naturalKeyValue(k)
                const subqId =
                  keyValue.subquestion_id ?
                    subqIdMap ? getMappedIdOrErr(keyValue.subquestion_id, 'subquestion')
                    : ok({id: keyValue.subquestion_id})
                  : ok({id: undefined})
                if (!subqId.ok) {
                  console.warn(`subquestion id not found for forecast ${k}`, {keyValue, subqIdMap})
                  return undefined
                }
                const createdById =
                  profileIdMap ? getMappedIdOrErr(keyValue.created_by, 'profile') : ok({id: keyValue.created_by})
                if (!createdById.ok) {
                  console.warn(`created_by id not found for forecast ${k}`, {keyValue, profileIdMap})
                  return undefined
                }
                const questionId =
                  questionIdMap ? getMappedIdOrErr(keyValue.question_id, 'question') : ok({id: keyValue.question_id})
                if (!questionId.ok) {
                  console.warn(`question id not found for forecast ${k}`, {keyValue, questionIdMap})
                  return undefined
                }
                const newKey = Forecast.naturalKey({
                  ...keyValue,
                  question_id: questionId.id,
                  subquestion_id: subqId.id,
                  created_by: createdById.id
                })
                return [newKey, v] as [string, QuestionForecastPub]
              }).filter(notNull)
            )
          }
        }
        if (subqIdMap) {
          cfg.subquestionOrder = cfg.subquestionOrder
            ?.map(k => {
              const subqId = getMappedIdOrErr(k, 'subquestion')
              if (!subqId.ok) {
                console.warn(`subquestion id not found for subquestion order ${k}`)
                return undefined
              }
              return subqId.id
            })
            .filter(notNull)

          cfg.resolutionValues = fromEntries(
            mapEntries(cfg.resolutionValues, (k, v) => {
              const key = Resolution.resolutionKey(k) // deserialize the key
              if (!key.subquestion_id) return [k, v] as [string, ResolutionValue]
              const subqId = getMappedIdOrErr(key.subquestion_id, 'subquestion')
              if (!subqId.ok) {
                console.warn(`subquestion id not found for resolution value ${k}`)
                return undefined
              }
              const newKeyString = Resolution.resolutionKeyString({...key, subquestion_id: subqId.id}) // serialize the new key
              return [newKeyString, v] as [string, ResolutionValue]
            }).filter(notNull)
          )
        }
        return cfg
      })
      .with('subquestion', 'forecast', 'comment', 'reaction', 'profile', () => undefined)
      .exhaustive()
    if (newCfg) newRow.cfg = newCfg
  }

  if (ensureIdMapped && newRow.id == oldId) return err(`when making a new ${table} row, the id was not mapped`)

  return ok(newRow)
}
